import React, { useEffect, useState } from 'react';
import {
  X,
  Lock,
  Building2,
  User as UserIcon,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Key,
  Save
} from 'lucide-react';
import { User, Company, Customer } from '@/types';
import { api, type LoginResult } from '@/services/api';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { createId } from '@/lib/ids';
import { validateUkPhone } from '@/lib/validators';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  validatePasswordPolicy,
  friendlyPasswordError,
  PASSWORD_POLICY_HINT
} from '@/lib/passwordPolicy';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: 'staff' | 'company' | 'customer';
  onLoginSuccess: (user: User | null, company: Company | null, customer: Customer | null) => void;
  onForgotPassword?: (email: string) => void;
  onSignInLinkSent?: (email: string) => void;
  onSwitchContext?: (context: 'staff' | 'company' | 'customer') => void;
  users: User[];
  companies: Company[];
  customers: Customer[];
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  context,
  onLoginSuccess,
  onForgotPassword,
  onSignInLinkSent,
  onSwitchContext
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSsoLoggingIn, setIsSsoLoggingIn] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isSendingLoginLink, setIsSendingLoginLink] = useState(false);
  const [isCompanyRegisterOpen, setIsCompanyRegisterOpen] = useState(false);
  const [isRegisteringCompany, setIsRegisteringCompany] = useState(false);
  const [companyRegisterError, setCompanyRegisterError] = useState('');
  const [companyPhoneTouched, setCompanyPhoneTouched] = useState(false);
  const [companyRegisterData, setCompanyRegisterData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    address: '',
    phone: ''
  });

  const [mustChangePassTarget, setMustChangePassTarget] = useState<{
    id: string;
    name: string;
    type: 'company' | 'customer';
    memberId?: string;
  } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPass, setIsUpdatingPass] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!isOpen) {
      setIsLoggingIn(false);
      setIsSsoLoggingIn(false);
      return;
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setIsSsoLoggingIn(false);
      }
    };

    const handlePageShow = () => {
      setIsSsoLoggingIn(false);
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4000);
  };

  const applyLoginResultToContext = (
    result: LoginResult,
    currentContext: 'staff' | 'company' | 'customer'
  ) => {
    if ('error' in result) {
      setError(result.error || 'An unexpected login error occurred.');
      return false;
    }

    if (currentContext === 'staff' && 'user' in result) {
      onLoginSuccess(result.user || null, null, null);
      return true;
    }

    if (currentContext === 'company' && 'company' in result) {
      onLoginSuccess(null, result.company || null, null);
      return true;
    }

    if (currentContext === 'customer' && 'customer' in result) {
      onLoginSuccess(null, null, result.customer || null);
      return true;
    }

    setError(`Unexpected login response for ${currentContext}.`);
    return false;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);

    try {
      const result = await api.login(email, password, context);

      applyLoginResultToContext(result, context);
    } catch (err: unknown) {
      console.error('Auth Logic Failure:', err);
      const message = `System error: ${normalizeErrorMessage(err, 'Unknown')}`;
      setError(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (context !== 'staff') return;
    setError('');
    setIsSsoLoggingIn(true);
    try {
      if (import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
        const result = await api.devBypassLogin();
        const ok = applyLoginResultToContext(result, 'staff');
        if (!ok) setIsSsoLoggingIn(false);
        return;
      }
      await api.loginWithMicrosoft();
    } catch (err: unknown) {
      const message = normalizeErrorMessage(err, 'Failed to start Microsoft sign-in.');
      setError(message);
      setIsSsoLoggingIn(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const policyError = validatePasswordPolicy(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }

    setIsUpdatingPass(true);
    setError('');

    try {
      if (mustChangePassTarget?.type === 'company') {
        await api.updateCompanyPassword(
          mustChangePassTarget.id,
          mustChangePassTarget.memberId!,
          newPassword
        );
        const result = await api.login(email, newPassword, 'company');
        applyLoginResultToContext(result, 'company');
      } else if (mustChangePassTarget?.type === 'customer') {
        await api.updateCustomerPassword(mustChangePassTarget.id, newPassword);
        const result = await api.login(email, newPassword, 'customer');
        applyLoginResultToContext(result, 'customer');
      }
    } catch (err: unknown) {
      setError(friendlyPasswordError(err));
    } finally {
      setIsUpdatingPass(false);
    }
  };

  const handleForgotPassword = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your email address first.');
      return;
    }

    setIsSendingReset(true);
    setError('');

    try {
      await api.requestPasswordReset(cleanEmail, `${window.location.origin}/?mode=reset`);
      showToast(
        'success',
        "If that email exists, we've sent a password reset code. Please check your inbox."
      );
      onForgotPassword?.(cleanEmail);
    } catch (err: unknown) {
      showToast('error', normalizeErrorMessage(err, 'Failed to send password reset email.'));
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleCustomerLoginLink = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your email address first.');
      return;
    }

    setIsSendingLoginLink(true);
    setError('');

    try {
      const result = await api.requestCustomerLoginLink(
        cleanEmail,
        `${window.location.origin}${window.location.pathname}`
      );
      if (result?.status === 'company_account') {
        // Company portal users sign in with a password, not a code. Move them
        // to the company sign-in rather than pretending a code was emailed.
        onSwitchContext?.('company');
        showToast(
          'error',
          'This email has Company Portal access. Sign in here with your password instead of a code.'
        );
        return;
      }
      if (result?.status === 'shared_email') {
        // Several candidates were booked under this address, so a code cannot
        // pick which of them is signing in. K2 links the right record by hand.
        showToast(
          'error',
          'This email is used for more than one person’s bookings, so we cannot send a sign-in code to it. Please contact K2 Occupational Health to have your own record set up.'
        );
        return;
      }
      if (result?.status === 'account_exists') {
        showToast(
          'error',
          "An account with this email already exists. Please sign in or use 'Forgot password' to regain access."
        );
        return;
      }
      showToast(
        'success',
        "If that email exists, we've sent a sign-in code. Please check your inbox."
      );
      // Hand off to the dedicated sign-in-code modal (closes this login modal).
      onSignInLinkSent?.(cleanEmail);
    } catch (err: unknown) {
      showToast('error', normalizeErrorMessage(err, 'Failed to send sign-in code.'));
    } finally {
      setIsSendingLoginLink(false);
    }
  };

  const openCompanyRegister = () => {
    setCompanyRegisterError('');
    setCompanyPhoneTouched(false);
    setCompanyRegisterData((prev) => ({
      ...prev,
      email: prev.email || email
    }));
    setIsCompanyRegisterOpen(true);
  };

  const closeCompanyRegister = () => {
    if (isRegisteringCompany) return;
    setIsCompanyRegisterOpen(false);
    setCompanyPhoneTouched(false);
  };

  const handleCompanyRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setCompanyRegisterError('');

    const name = companyRegisterData.name.trim();
    const contactPerson = companyRegisterData.contactPerson.trim();
    const emailValue = companyRegisterData.email.trim().toLowerCase();
    const address = companyRegisterData.address.trim();
    const phone = companyRegisterData.phone.trim();

    if (!name || !contactPerson || !emailValue || !address) {
      setCompanyRegisterError('Please complete all required fields.');
      return;
    }

    if (phone && !validateUkPhone(phone).valid) {
      setCompanyPhoneTouched(true);
      setCompanyRegisterError('Please enter a valid UK phone number.');
      return;
    }

    setIsRegisteringCompany(true);
    try {
      const newCompany: Company = {
        id: createId(),
        name,
        contactPerson,
        email: emailValue,
        address,
        phone,
        status: 'pending',
        tier: 'bronze',
        paymentPreference: 'invoice',
        createdAt: new Date().toISOString(),
        changeLogs: []
      };
      await api.registerCompany(newCompany);
      showToast(
        'success',
        'Registration submitted. We will email a temporary password once approved.'
      );
      setEmail(emailValue);
      setCompanyRegisterData({
        name: '',
        contactPerson: '',
        email: '',
        address: '',
        phone: ''
      });
      setCompanyPhoneTouched(false);
      setIsCompanyRegisterOpen(false);
    } catch (err: unknown) {
      setCompanyRegisterError(
        normalizeErrorMessage(err, 'Registration failed. Please check your details and try again.')
      );
    } finally {
      setIsRegisteringCompany(false);
    }
  };

  if (mustChangePassTarget) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
        <div className="bg-white rounded-xl shadow-xl p-6 max-sm w-full relative border border-slate-200">
          <div className="text-center mb-4">
            <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Key className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-800">Security Update</h2>
            <p className="text-xs text-slate-500 mt-1">
              Please set a secure password for <strong>{mustChangePassTarget.name}</strong>.
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                New Password
              </label>
              <input
                type="password"
                required
                autoFocus
                className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="text-[10px] text-slate-400 mt-1">{PASSWORD_POLICY_HINT}</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                required
                className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded">{error}</p>
            )}
            <button
              disabled={isUpdatingPass}
              className="w-full bg-slate-900 text-white py-2.5 rounded-lg font-bold hover:bg-slate-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50 text-sm"
            >
              {isUpdatingPass ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save & Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in text-slate-900">
      <div className="bg-white rounded-2xl shadow-2xl p-5 max-w-[340px] w-full max-h-[90vh] overflow-y-auto border border-slate-200">
        <div className="flex justify-end">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {toast && (
          <div
            className={`mb-3 rounded-lg px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
              toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
            }`}
          >
            {toast.message}
          </div>
        )}

        <div className="flex flex-col items-center mb-5">
          <div className="flex items-center gap-2 mb-1">
            {context === 'staff' && <Lock className="w-5 h-5 text-slate-600" />}
            {context === 'company' && <Building2 className="w-5 h-5 text-purple-600" />}
            {context === 'customer' && <UserIcon className="w-5 h-5 text-blue-600" />}
            <h2 className="text-lg font-bold text-slate-800">
              {context === 'staff'
                ? 'K2 Staff Portal'
                : context === 'company'
                  ? 'Company Portal'
                  : 'Patient Portal'}
            </h2>
          </div>
          <div className="flex items-center gap-1 text-[9px] font-black text-teal-600 uppercase bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100 tracking-wider">
            <ShieldCheck className="w-3 h-3" /> Secure Database Access
          </div>
        </div>

        {context !== 'staff' ? (
          <>
            <form onSubmit={handleLogin} className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  disabled={isLoggingIn || isSsoLoggingIn}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Password
                </label>
                <PasswordInput
                  required
                  className="rounded-lg p-2 text-sm focus:ring-teal-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={isLoggingIn || isSsoLoggingIn}
                />
              </div>
              {error && (
                <div className="bg-red-50 text-red-600 p-2 rounded-lg text-xs border border-red-100">
                  <p className="font-bold flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Authentication Error
                  </p>
                  <p className="mt-0.5 ml-4 opacity-80">{error}</p>
                </div>
              )}
              <button
                disabled={isLoggingIn || isSsoLoggingIn}
                className={`w-full text-white py-2.5 rounded-lg font-bold text-sm hover:opacity-90 transition-opacity flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  context === 'company' ? 'bg-purple-600' : 'bg-blue-600'
                }`}
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Authenticating...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            {context === 'company' && (
              <div className="mt-3 flex flex-col items-center gap-2 text-center">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isSendingReset || isLoggingIn}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  {isSendingReset ? 'Sending reset code...' : 'Forgot password?'}
                </button>
                <button
                  type="button"
                  onClick={openCompanyRegister}
                  disabled={isLoggingIn}
                  className="text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-50"
                >
                  Register your company
                </button>
              </div>
            )}
            {context === 'customer' && (
              <div className="mt-3 flex flex-col items-center gap-2 text-center">
                <button
                  type="button"
                  onClick={handleCustomerLoginLink}
                  disabled={isSendingLoginLink || isLoggingIn}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  {isSendingLoginLink ? 'Sending sign-in code...' : 'Email me a sign-in code'}
                </button>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isSendingReset || isLoggingIn}
                  className="text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-50"
                >
                  {isSendingReset ? 'Sending reset code...' : 'Forgot password?'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500 text-center">Staff login uses Microsoft SSO.</p>
            {error && (
              <div className="bg-red-50 text-red-600 p-2 rounded-lg text-xs border border-red-100">
                <p className="font-bold flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Authentication Error
                </p>
                <p className="mt-0.5 ml-4 opacity-80">{error}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={isSsoLoggingIn}
              className="w-full border border-slate-200 bg-white text-slate-700 py-2.5 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSsoLoggingIn ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Microsoft...
                </>
              ) : (
                'Continue with Microsoft'
              )}
            </button>
          </div>
        )}
      </div>

      {context === 'company' && isCompanyRegisterOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="company-register-title"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200"
          >
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 id="company-register-title" className="font-bold text-slate-800">
                Company Registration
              </h3>
              <button
                type="button"
                onClick={closeCompanyRegister}
                className="text-slate-400 hover:text-slate-600"
                disabled={isRegisteringCompany}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCompanyRegister} className="p-5 space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Company Name
                </label>
                <input
                  required
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50"
                  value={companyRegisterData.name}
                  onChange={(e) =>
                    setCompanyRegisterData((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Contact Person
                </label>
                <input
                  required
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50"
                  value={companyRegisterData.contactPerson}
                  onChange={(e) =>
                    setCompanyRegisterData((prev) => ({
                      ...prev,
                      contactPerson: e.target.value
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50"
                  value={companyRegisterData.email}
                  onChange={(e) =>
                    setCompanyRegisterData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Phone (optional)
                </label>
                <input
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50"
                  value={companyRegisterData.phone}
                  onChange={(e) =>
                    setCompanyRegisterData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setCompanyPhoneTouched(true);
                    const phoneResult = value ? validateUkPhone(value) : null;
                    if (phoneResult && phoneResult.valid) {
                      setCompanyRegisterData((prev) => ({
                        ...prev,
                        phone: phoneResult.normalized
                      }));
                    }
                  }}
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  UK numbers only, e.g. 07123 456789 or +44 7123 456789.
                </p>
                {companyPhoneTouched &&
                  companyRegisterData.phone.trim() &&
                  !validateUkPhone(companyRegisterData.phone.trim()).valid && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      Enter a valid UK phone number.
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">
                  Full Address
                </label>
                <textarea
                  required
                  className="w-full border rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-slate-50 min-h-[90px]"
                  value={companyRegisterData.address}
                  onChange={(e) =>
                    setCompanyRegisterData((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </div>
              <p className="text-xs text-slate-500">
                We will review your registration and email a temporary password once approved.
              </p>
              {companyRegisterError && (
                <div className="bg-red-50 text-red-600 p-2 rounded-lg text-xs border border-red-100">
                  {companyRegisterError}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCompanyRegister}
                  disabled={isRegisteringCompany}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isRegisteringCompany}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isRegisteringCompany ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    'Submit Registration'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
